import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiChevronLeft, FiMapPin, FiCreditCard, FiTruck, FiPackage, FiCheck } from 'react-icons/fi';
import { getOrder } from '../../services/api';
import './OrderDetailPage.css';

const TIMELINE_STEPS = [
  { key: 'placed',     label: 'Order Placed',    icon: <FiPackage size={16} /> },
  { key: 'confirmed',  label: 'Confirmed',        icon: <FiCheck size={16} /> },
  { key: 'processing', label: 'Processing',       icon: <FiPackage size={16} /> },
  { key: 'shipped',    label: 'Shipped',          icon: <FiTruck size={16} /> },
  { key: 'delivered',  label: 'Delivered',        icon: <FiCheck size={16} /> },
];

const STATUS_ORDER = ['placed', 'confirmed', 'processing', 'shipped', 'delivered'];

const STATUS_CONFIG = {
  placed:      { cls: 'badge-info' },
  confirmed:   { cls: 'badge-primary' },
  processing:  { cls: 'badge-warning' },
  shipped:     { cls: 'badge-accent' },
  delivered:   { cls: 'badge-success' },
  cancelled:   { cls: 'badge-danger' },
};

const DEMO_ORDER = {
  _id: 'o1',
  orderNumber: 'MH-ABC123-XY1',
  createdAt: '2026-06-01T10:30:00Z',
  orderStatus: 'shipped',
  paymentMethod: 'cod',
  paymentStatus: 'pending',
  subtotal: 1199.97,
  shippingCost: 0,
  discount: 0,
  total: 1199.97,
  shippingAddress: {
    fullName: 'Ushi Khan',
    phone: '+92 03001234567',
    street: '42 Market Street, Floor 3',
    city: 'Karachi',
    state: 'Sindh',
    zipCode: '75500',
    country: 'Pakistan',
  },
  items: [
    { _id: 'i1', name: 'Premium Wireless Headphones', quantity: 1, price: 149.99, image: '', vendor: { storeName: 'TechZone' } },
    { _id: 'i2', name: 'Mechanical Gaming Keyboard RGB Backlit', quantity: 2, price: 524.99, image: '', vendor: { storeName: 'GearPro' } },
  ],
  tracking: { carrier: 'TCS Courier', trackingNumber: 'TCS123456789PK', trackingUrl: '', estimatedDelivery: '2026-06-13' },
  statusHistory: [
    { status: 'placed', note: 'Order received', updatedAt: '2026-06-01T10:30:00Z' },
    { status: 'confirmed', note: 'Payment confirmed', updatedAt: '2026-06-01T11:00:00Z' },
    { status: 'processing', note: 'Being packed', updatedAt: '2026-06-02T09:00:00Z' },
    { status: 'shipped', note: 'Dispatched via TCS Courier', updatedAt: '2026-06-03T14:00:00Z' },
  ],
};

const picsum = (seed) => `https://picsum.photos/seed/${seed}/56/56`;

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOrder(id);
      setOrder(data.order);
    } catch {
      setOrder(DEMO_ORDER);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  if (loading) return <div className="loading-center" style={{ minHeight: '60vh' }}><div className="spinner" /><span>Loading order…</span></div>;
  if (!order) return <div className="empty-state" style={{ minHeight: '60vh' }}><h3>Order not found</h3><Link to="/orders" className="btn btn-primary">Back to Orders</Link></div>;

  const currentIdx = STATUS_ORDER.indexOf(order.orderStatus);
  const isCancelled = order.orderStatus === 'cancelled';
  const { cls } = STATUS_CONFIG[order.orderStatus] || { cls: 'badge-info' };
  const orderDate = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="order-detail-page">
      <div className="container">
        {/* Back + title */}
        <div className="od-header">
          <Link to="/orders" className="od-back">
            <FiChevronLeft size={18} /> Back to Orders
          </Link>
          <div className="od-title-row">
            <div>
              <h1 className="od-order-num">{order.orderNumber}</h1>
              <p className="od-date text-muted">{orderDate}</p>
            </div>
            <span className={`badge ${cls}`} style={{ fontSize: '0.85rem', padding: '0.3rem 0.875rem' }}>
              {order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1)}
            </span>
          </div>
        </div>

        <div className="od-layout">
          <div className="od-main">
            {/* Timeline */}
            {!isCancelled && (
              <div className="card od-section">
                <div className="card-header"><h2 className="od-section-title">Order Status</h2></div>
                <div className="card-body">
                  <div className="timeline">
                    {TIMELINE_STEPS.map((step, idx) => {
                      const done = idx < currentIdx;
                      const active = idx === currentIdx;
                      const historyEntry = order.statusHistory?.find((h) => h.status === step.key);
                      const stepDate = historyEntry
                        ? new Date(historyEntry.updatedAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : null;

                      return (
                        <div key={step.key} className={`timeline-step ${done ? 'done' : active ? 'active' : 'pending'}`}>
                          <div className="timeline-left">
                            <div className="timeline-dot">{done ? <FiCheck size={12} /> : step.icon}</div>
                            {idx < TIMELINE_STEPS.length - 1 && <div className="timeline-line" />}
                          </div>
                          <div className="timeline-content">
                            <p className="timeline-label">{step.label}</p>
                            {stepDate && <p className="timeline-date">{stepDate}</p>}
                            {historyEntry?.note && <p className="timeline-note">{historyEntry.note}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Tracking */}
            {order.tracking?.trackingNumber && (
              <div className="card od-section">
                <div className="card-header"><h2 className="od-section-title"><FiTruck size={16} /> Tracking</h2></div>
                <div className="card-body tracking-panel">
                  <div className="tracking-row">
                    <span className="tracking-label">Carrier</span>
                    <span className="tracking-value">{order.tracking.carrier}</span>
                  </div>
                  <div className="tracking-row">
                    <span className="tracking-label">Tracking #</span>
                    <span className="tracking-value tracking-num">{order.tracking.trackingNumber}</span>
                  </div>
                  {order.tracking.estimatedDelivery && (
                    <div className="tracking-row">
                      <span className="tracking-label">Est. Delivery</span>
                      <span className="tracking-value">
                        {new Date(order.tracking.estimatedDelivery).toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {order.tracking.trackingUrl && (
                    <a href={order.tracking.trackingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ marginTop: '0.5rem', alignSelf: 'flex-start' }}>
                      Track on Carrier Site →
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Items table */}
            <div className="card od-section">
              <div className="card-header"><h2 className="od-section-title">Items Ordered</h2></div>
              <div className="table-wrapper" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Vendor</th>
                      <th style={{ textAlign: 'center' }}>Qty</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <div className="od-item-cell">
                            <img
                              src={item.image || picsum(item.name)}
                              alt={item.name}
                              className="od-item-img"
                              onError={(e) => { e.target.src = picsum(item.name); }}
                            />
                            <span className="od-item-name">{item.name}</span>
                          </div>
                        </td>
                        <td className="text-muted text-sm">{item.vendor?.storeName || '—'}</td>
                        <td style={{ textAlign: 'center' }}>×{item.quantity}</td>
                        <td style={{ textAlign: 'right' }}>Rs. {Number(item.price).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>Rs. {(item.price * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar panels */}
          <div className="od-sidebar">
            {/* Order total */}
            <div className="card">
              <div className="card-header"><h3 className="od-section-title">Order Summary</h3></div>
              <div className="card-body od-totals">
                <div className="od-total-row"><span>Subtotal</span><span>Rs. {Number(order.subtotal).toFixed(2)}</span></div>
                <div className="od-total-row"><span>Shipping</span><span>{order.shippingCost === 0 ? <span className="text-success">FREE</span> : `Rs. ${order.shippingCost}`}</span></div>
                {order.discount > 0 && (
                  <div className="od-total-row text-success"><span>Discount</span><span>-Rs. {Number(order.discount).toFixed(2)}</span></div>
                )}
                <div className="od-divider" />
                <div className="od-total-row od-grand"><span>Total</span><span>Rs. {Number(order.total).toFixed(2)}</span></div>
              </div>
            </div>

            {/* Shipping address */}
            <div className="card">
              <div className="card-header"><h3 className="od-section-title"><FiMapPin size={14} /> Shipping Address</h3></div>
              <div className="card-body od-address">
                <p><strong>{order.shippingAddress?.fullName}</strong></p>
                <p>{order.shippingAddress?.phone}</p>
                <p>{order.shippingAddress?.street}</p>
                <p>{order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zipCode}</p>
                <p>{order.shippingAddress?.country}</p>
              </div>
            </div>

            {/* Payment */}
            <div className="card">
              <div className="card-header"><h3 className="od-section-title"><FiCreditCard size={14} /> Payment</h3></div>
              <div className="card-body od-payment">
                <div className="od-total-row">
                  <span>Method</span>
                  <span>{order.paymentMethod === 'cod' ? '💵 Cash on Delivery' : `💳 ${order.paymentMethod}`}</span>
                </div>
                <div className="od-total-row">
                  <span>Status</span>
                  <span className={`badge ${order.paymentStatus === 'paid' ? 'badge-success' : order.paymentStatus === 'failed' ? 'badge-danger' : 'badge-warning'}`}>
                    {order.paymentStatus?.charAt(0).toUpperCase() + order.paymentStatus?.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
